import '@fe-toolkit/web-components/fonts.css'
import '@fe-toolkit/web-components/base-styles.css'
import { defineRuntimeConfig } from '@modern-js/runtime'
import { setupObservability } from '@fe-toolkit/observability/client'

import { basePath, mfeName } from '@/config/runtimeConfig'

import { version } from '../package.json'

// Override webpack's public path for CDN-enabled environments so that lazy-loaded
// route chunks (dynamic imports) are fetched from the CDN rather than the pod.
//
// Background: `output.assetPrefix` in modern.config.ts is a BUILD-TIME value. With
// "build once, deploy anywhere", CDN_URL is not set during CI so the bundle bakes in
// `__webpack_require__.p = '/'`. This means all lazy chunks would load from the pod
// even when CDN is enabled.
//
// Fix: update __webpack_public_path__ (rspack's alias for __webpack_require__.p) early
// in the entry module. window.__RUNTIME_CONFIG__ is already available here because
// src/routes/layout.tsx renders it as an inline <script> *before* <Scripts /> in
// Document.tsx, so it is set in the SSR HTML before the main bundle executes.
//
// The assignment must happen before any dynamic import() is triggered. Since Modern.js
// router loads all routes lazily, this module-init assignment is always early enough.
if (typeof window !== 'undefined') {
  const cfg = window.__RUNTIME_CONFIG__

  if (cfg?.CDN_URL && mfeName) {
    const cdnBase = cfg.CDN_URL.replace(/\/$/, '')

    // __webpack_public_path__ is a magic rspack/webpack variable. Inside a bundled
    // module, rspack compiles this assignment to __webpack_require__.p = ...
    // The trailing slash is required by the webpack public path convention.
    __webpack_public_path__ = `${cdnBase}/${mfeName}/`
  }
}

// Initialise client-side observability (logging + tracing) synchronously at module-init
// time so the real OTel LoggerProvider is registered before any createLogger() call
// captures it. createLogger() stores a reference to the provider at creation time (not
// lazily), so calling setupObservability() inside a useEffect() is too late — the
// module-level logger instances always end up with the NoopLoggerProvider.
if (typeof window !== 'undefined') {
  const cfg = window.__RUNTIME_CONFIG__
  const observabilityBaseUrl = cfg?.OBSERVABILITY_BASE_URL || ''

  if (cfg && observabilityBaseUrl) {
    setupObservability({
      mfeName: mfeName ? `${mfeName}-client` : 'unknown-mfe',
      version: version || '0.0.0',
      tracesUrl: `${observabilityBaseUrl}/v1/traces`,
      metricsUrl: `${observabilityBaseUrl}/v1/metrics`,
      logsUrl: `${observabilityBaseUrl}/v1/logs`,
      enableMetrics: true,
    })
  }
}

// MSW is only included when ENABLE_MOCKS=true is set at BUILD TIME (or in dev).
//
// Using build-time process.env.ENABLE_MOCKS (exposed via globalVars in modern.config.ts)
// allows Rspack to statically eliminate the dynamic import expression when mocking is
// disabled. This prevents the MSW/faker/testing chunk (~762KB) from being bundled or
// preloaded in production builds, which was a significant TBT contributor.
//
// For E2E test builds that need browser-side mocking: run `ENABLE_MOCKS=true pnpm build`.
// Runtime-only ENABLE_MOCKS (window.__RUNTIME_CONFIG__) cannot drive this import because
// Rspack evaluates the module graph at build time — a runtime-only guard still causes the
// chunk to be generated and added to the SSR HTML preload list.
if ((process.env.NODE_ENV === 'development' || process.env.ENABLE_MOCKS === 'true') && typeof window !== 'undefined') {
  import('../shared/msw/browser').then(({ enableMocking, worker }) =>
    enableMocking(worker, { serviceWorkerUrl: '/mockServiceWorker.js' })
  )
}

export default defineRuntimeConfig({
  router: {
    basename: basePath || undefined,
  },
})
